import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Table from '@mui/material/Table';

import type { SurveyListItem } from 'src/types/survey';
import { useSurveyList } from 'src/components/survey/SurveyContext/ListContext';

import { COLUMNS, COLUMNS_LENGTH, COLUMNS_LIST } from './constants';
import SurveyTableRow from './SurveyTableRow';
import TableHeaderCell from './TableHeaderCell';
import TablePagination from './TablePagination';

import { styles } from './index.styles';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const SurveySelectionTable: FC = () => {
  const { t } = useTranslation('survey');
  const { surveys, loading } = useSurveyList();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const paginatedSurveys = surveys.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box
      sx={{
        height: '100%',
      }}
    >
      <TableContainer sx={styles.tableContainer}>
        <Table stickyHeader padding="none">
          <TableHead>
            <TableRow>
              {COLUMNS_LIST.map(([key, { icon: Icon, headline, sort }], i) => (
                <TableHeaderCell
                  key={i}
                  sortKey={sort && (key as keyof SurveyListItem)}
                >
                  {!!Icon && <Icon />}
                  {headline ? t(`tableHeader.${key}`) : ''}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS_LENGTH}
                  sx={{ textAlign: 'center' }}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedSurveys.length > 0 ? (
              paginatedSurveys.map((survey, i) => (
                <SurveyTableRow key={i} survey={survey} columns={COLUMNS} />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS_LENGTH}
                  sx={{ textAlign: 'center' }}
                >
                  <Typography>{t('noSurveysFound')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={COLUMNS_LENGTH} sx={styles.footer}>
                <TablePagination
                  count={surveys.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Results per page"
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};
export default SurveySelectionTable;
